import type { ReactNode } from "react";

/**
 * Kabuğun çizgi ikon seti.
 *
 * ### Neden bir paket değil
 *
 * Bir ikon kütüphanesi eklemek yeni bir bağımlılık ve ADR ister (refleks 9: *"50 satırda
 * kendimiz yazar mıydık?"*). Burada gereken sayı on beş; hepsi 24'lük kılavuz üzerinde
 * çizilmiş, `currentColor` ile boyanan ve `stroke-width` paylaşan tek bir aile.
 *
 * ### Neden `packages/ui`'de değil
 *
 * Hangi ikonun hangi domaini anlattığı DOMAIN BİLGİSİ; `packages/ui/CLAUDE.md` bunu
 * pakete sokmayı yasaklıyor. Kit ikonu bir `ReactNode` olarak alıyor, hangisi olduğunu
 * bilmiyor.
 *
 * ### Erişilebilirlik
 *
 * Hepsi `aria-hidden`: ikon bir SÜS. Anlamı taşıyan şey yanındaki etiket ya da düğmenin
 * `aria-label`'ı; ikonu ayrıca duyurmak kullanıcıya aynı şeyi iki kez söyletirdi.
 */
function Icon({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className ?? "size-5"}
    >
      {children}
    </svg>
  );
}

export type IconProps = { className?: string };

export function HomeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 10.5 12 4l8 6.5" />
      <path d="M6 9.8V19a1 1 0 0 0 1 1h3.5v-4.5h3V20H17a1 1 0 0 0 1-1V9.8" />
    </Icon>
  );
}

export function ContractIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4Z" />
      <path d="M14 3v4h4" />
      <path d="M9 12h6M9 16h4" />
    </Icon>
  );
}

export function ClientsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.6" />
      <path d="M17.5 13.6a5.2 5.2 0 0 1 3 5.4" />
    </Icon>
  );
}

export function ClientAreaIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
      <path d="M3 12h18" />
    </Icon>
  );
}

export function OrganizationIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20V6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v14" />
      <path d="M14 10h5a1 1 0 0 1 1 1v9" />
      <path d="M2.5 20h19" />
      <path d="M7 9h3M7 13h3M17 14h1" />
    </Icon>
  );
}

export function SubscriptionIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20V13M9.5 20V8M15 20v-5M20.5 20V5" />
    </Icon>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v2M12 19.2v2M4.5 7.5l1.7 1M17.8 15.5l1.7 1M4.5 16.5l1.7-1M17.8 8.5l1.7-1" />
    </Icon>
  );
}

export function CollapseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11 7.5 6.5 12l4.5 4.5" />
      <path d="M17.5 7.5 13 12l4.5 4.5" />
    </Icon>
  );
}

export function ExpandIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13 7.5 17.5 12 13 16.5" />
      <path d="M6.5 7.5 11 12l-4.5 4.5" />
    </Icon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="6" />
      <path d="m15.5 15.5 4 4" />
    </Icon>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5.5v13M5.5 12h13" />
    </Icon>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 3.4.9 5 1.7 5.8a.6.6 0 0 1-.4 1H5.2a.6.6 0 0 1-.4-1c.8-.8 1.7-2.4 1.7-5.8Z" />
      <path d="M10.2 19.5a2 2 0 0 0 3.6 0" />
    </Icon>
  );
}

export function TaskIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="5" y="5" width="14" height="16" rx="2" />
      <path d="M9.5 5V3.8a.8.8 0 0 1 .8-.8h3.4a.8.8 0 0 1 .8.8V5" />
      <path d="m9.5 13 1.8 1.8 3.4-3.6" />
    </Icon>
  );
}

export function WorkspaceIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 9.5h16M9.5 9.5V20" />
    </Icon>
  );
}

export function ShieldIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3.2 5 5.8v5.4c0 4.2 2.8 7.6 7 9.6 4.2-2 7-5.4 7-9.6V5.8l-7-2.6Z" />
      <path d="m9.2 11.9 2 2 3.6-3.8" />
    </Icon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8.2 10.5V8a3.8 3.8 0 0 1 7.6 0v2.5" />
    </Icon>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="m3.6 6.8 8.4 6 8.4-6" />
    </Icon>
  );
}

export function AlertIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10.9 4.3 3.3 17.4a1.3 1.3 0 0 0 1.1 1.9h15.2a1.3 1.3 0 0 0 1.1-1.9L13.1 4.3a1.3 1.3 0 0 0-2.2 0Z" />
      <path d="M12 9.5v4M12 16.6h.01" />
    </Icon>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m7 10 5 5 5-5" />
    </Icon>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="5.5" width="16" height="15" rx="2" />
      <path d="M4 10h16M8.5 3.5v3M15.5 3.5v3" />
    </Icon>
  );
}

export function UploadIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 15.5V4M12 4 7.5 8.5M12 4l4.5 4.5" />
      <path d="M5 15.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2.5" />
    </Icon>
  );
}

export function TemplateIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="4" width="7" height="7" rx="1.3" />
      <rect x="13" y="4" width="7" height="7" rx="1.3" />
      <rect x="4" y="13" width="7" height="7" rx="1.3" />
      <rect x="13" y="13" width="7" height="7" rx="1.3" />
    </Icon>
  );
}

/**
 * AI/üretim ikonu — "AI ile taslak" yöntemi (CLOS-239 `[187]`).
 *
 * ⚠ Diğerleriyle AYNI `Icon` sarmalayıcısı: kalınlık, kapak biçimi ve 24'lük kutu tek
 * yerden geliyor; ayrı bir svg yazsaydık bu ikon ötekilerin yanında farklı ağırlıkta
 * dururdu.
 */
export function SparklesIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4.5 13.6 9l4.4 1.6-4.4 1.6L12 16.7l-1.6-4.5L6 10.6 10.4 9 12 4.5Z" />
      <path d="M18 15.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z" />
    </Icon>
  );
}
